import React, {createContext, useContext, useState} from 'react';
import {useQuery, useMutation} from '@apollo/react-hooks';

import {
  CurrentUserContextProviderValue,
  UserFields,
  EditableUserFields,
  CurrentUserContextProviderProps,
  UserFromGraphql,
} from './types';
import {
  UPDATE_CURRENT_USER_MUTATION,
  CURRENT_USER_QUERY,
  LOGOUT_USER_MUTATION,
  UPDATE_APPLE_MUSIC_TOKEN_MUTATION,
} from './graphql';
import {
  LogoutUserMutation,
  UpdateCurrentUserMutation,
  UpdateCurrentUserMutationVariables,
  CurrentUserQuery,
  UpdateAppleMusicTokenMutation,
  UpdateAppleMusicTokenMutationVariables,
} from './graphql/types';
import {useMusicKitContext} from '../MusicKitProvider';

const CurrentUserContextObject = createContext<CurrentUserContextProviderValue | null>(
  null,
);

export function CurrentUserProvider({
  children,
}: CurrentUserContextProviderProps) {
  const [currentUser, _setCurrentUser] = useState<UserFields | null>(null);

  const setCurrentUser = (user: UserFields | null) => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.setItem('user', '');
    }
    _setCurrentUser(user);
  };

  const musicKit = useMusicKitContext();

  const updateUserFromGraphqlData = (
    userData: UserFromGraphql,
  ): CurrentUserContextProviderValue | null => {
    const updatedUser = extractUserFields(userData);
    setCurrentUser(updatedUser);
    return decorateUser(updatedUser);
  };

  // Triggers a refresh of the currentUser VIA Query
  const refresh = async () => {
    const {data, errors} = await refetch();
    if (errors) throw errors;
    if (!data?.me) return null;

    return updateUserFromGraphqlData(data.me);
  };

  // Updates the provided user properties VIA Mutation
  const update = async (
    changes: EditableUserFields,
  ): Promise<CurrentUserContextProviderValue | null> => {
    const {data, errors} = await updateUser({
      variables: {
        ...changes,
      },
    });

    if (errors) throw errors;
    if (!data?.updateAccount?.user) return null;

    return updateUserFromGraphqlData(data.updateAccount.user);
  };

  const logout = async () => {
    const {data, errors} = await logoutMutation();
    if (errors) throw errors;

    if (data?.logout?.loggedOut) {
      await musicKit!.unauthorize();
      setCurrentUser(null);
      return true;
    }
    return false;
  };

  const decorateUser = (userData: UserFields | null) => {
    if (!userData) {
      // Return without logged in user data but still provide modifier methods
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        return {
          ...userData,
          refresh,
          update,
          updateToken,
          logout,
          loggedIn: true,
        };
      }
      return {
        loggedIn: false,
        refresh,
        update,
        updateToken,
        logout,
      } as CurrentUserContextProviderValue;
    }

    return {
      ...userData,
      refresh,
      update,
      updateToken,
      logout,
      loggedIn: true,
    };
  };

  const updateToken = async (
    token: string,
  ): Promise<CurrentUserContextProviderValue | null> => {
    try {
      const response = await updateAppleMusicToken({variables: {token}});
      const updatedUser = response?.data?.updateAppleMusicToken?.user;

      if (updatedUser) {
        const extractedUser = extractUserFields(updatedUser);
        setCurrentUser(extractedUser);
        return decorateUser(extractedUser);
      }
    } catch (err) {
      console.log(err);
    }
    return null;
  };

  const [updateUser] = useMutation<
    UpdateCurrentUserMutation,
    UpdateCurrentUserMutationVariables
  >(UPDATE_CURRENT_USER_MUTATION);

  const [updateAppleMusicToken] = useMutation<
    UpdateAppleMusicTokenMutation,
    UpdateAppleMusicTokenMutationVariables
  >(UPDATE_APPLE_MUSIC_TOKEN_MUTATION);

  const {refetch} = useQuery<CurrentUserQuery>(CURRENT_USER_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: ({me}) => {
      if (me) {
        updateUserFromGraphqlData(me);
      }
    },
  });

  const [logoutMutation] = useMutation<LogoutUserMutation>(
    LOGOUT_USER_MUTATION,
  );

  // console.log('CurrentUserContext: Rerendered', history.location.state);
  return (
    <CurrentUserContextObject.Provider value={decorateUser(currentUser)}>
      {children}
    </CurrentUserContextObject.Provider>
  );
}

export function useCurrentUserContext() {
  const currentUserContext = useContext(CurrentUserContextObject);

  if (!currentUserContext) {
    // This will allow us to check if there is even any user data
    return {loggedIn: false} as CurrentUserContextProviderValue;
  }
  return currentUserContext;
}

function extractUserFields(user: UserFromGraphql) {
  if (!user) {
    throw new Error('Could not extract user fields from null object');
  }

  const {phoneNumber, id, fullName, username, appleMusicToken} = user;
  return {
    phoneNumber,
    id,
    appleMusicToken,
    fullName: fullName || '',
    username: username || '',
  };
}
